import { FC, useState } from 'react';

import logger from '@eneco/common/log';
import RichText from '@eneco/components/RichText/RichText';
import {
  deleteEnecoBeXapiSiteApiV1OrdersByOrderIdPromo,
  putEnecoBeXapiSiteApiV1OrdersByOrderIdPayment,
} from '@eneco/dc-be/client/services.gen';
import { unwrapValidationError } from '@eneco/dc-be/utils/unwrapError';
import { AlertDialog, Bucket, Button, Heading, InputSelect, NotificationBox, Stack } from '@eneco/sparky';
import { NotificationBoxProps } from '@eneco/sparky/types';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { OrderManagementWithItems } from '@sitecore/types/manual/be/OrderManagementWithItems';
import { useFormContext } from 'react-hook-form';
import { useSWRConfig } from 'swr';

import DataErrorNotification from '../../../components/DataErrorNotification';
import IbanNumberInput, { normalizeIban } from '../../../components/IbanNumberInput';
import { useOrderId } from '../hooks/useOrderId';
import { OrderManagementFormSchema } from '../types/orderManagementSchema';
import { FlowChildProps } from '../types/OrderManagementSteps';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';
import useCheckLossOfPromo from '../utils/useCheckLossOfPromo';

export const ORDER_MANAGEMENT_PAYMENT_METHODS = {
  DIRECT_DEBIT: 'DirectDebit',
  BANK_TRANSFER: 'BankTransfer',
};

export const SelectPaymentMethodStep: FC<FlowChildProps> = ({ nextMainStep }) => {
  const { fields } = useContent<OrderManagementWithItems>();
  const { paymentMethodStep: paymentMethodFields } = fields;
  const orderId = useOrderId();
  const { trackEventBE } = useTracking();

  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [showPromotionWarning, setShowPromotionWarning] = useState<boolean>(false);
  const [error, setError] = useState<NotificationBoxProps | null>(null);
  const [hasUpdateError, setHasUpdateError] = useState(false);
  const { mutate } = useSWRConfig();

  const {
    trigger,
    getValues,
    register,
    watch,
    formState: { errors },
  } = useFormContext<OrderManagementFormSchema>();

  const paymentMethod = watch('selectPaymentMethod.paymentMethod');

  const { hasLossOfPromo } = useCheckLossOfPromo({ paymentMethod });

  const checkDialogTrigger = () => {
    if (hasLossOfPromo) setShowPromotionWarning(true);
    else onNextClick();
  };

  const onNextClick = async () => {
    setShowPromotionWarning(false);
    if (!orderId) {
      setError({
        isAlert: true,
        variant: 'error',
        title: fields.overviewStep.errorNotification.value.title || 'Error [not in sitecore]',
        text: fields.overviewStep.errorNotification.value.content || '[not in sitecore]: Order ID is missing',
      });
      return;
    }

    const isValid = await trigger('selectPaymentMethod');
    if (!isValid) {
      return;
    }

    try {
      setIsLoadingSubmit(true);
      const selectPaymentMethod = getValues('selectPaymentMethod');

      if (orderId && hasLossOfPromo) {
        try {
          await deleteEnecoBeXapiSiteApiV1OrdersByOrderIdPromo({
            path: { orderId },
          });
          mutate(`/orders/${orderId}/simulate`);
        } catch (error) {
          logger.error('Pr0m0D', 'Failed to remove promo after user confirmed loss', { error });
        }
      }

      await putEnecoBeXapiSiteApiV1OrdersByOrderIdPayment({
        path: {
          orderId,
        },
        body: {
          paymentMethod: selectPaymentMethod.paymentMethod,
          acceptedMandate: true,
          iban: normalizeIban(selectPaymentMethod.ibanNumber || ''),
        },
      }).then(result => {
        const { response, error: resultError } = result;
        unwrapValidationError(resultError);
        setIsLoadingSubmit(false);
        if (response.ok) {
          trackEventBE('be_om_select_payment_method', {
            status: 'success',
            data: getTrackableFormValues(getValues()),
          });
          nextMainStep();
        } else {
          setError({
            isAlert: true,
            variant: 'error',
            title: fields.overviewStep.errorNotification.value.title || 'Error [not in sitecore]',
            text:
              fields.overviewStep.errorNotification.value.content ||
              '[not in sitecore]: Je moet de correcte velden invullen',
          });
          trackEventBE('be_om_select_payment_method', {
            status: 'failed',
            message: 'Failed to update order with selected payment method',
            data: getTrackableFormValues(getValues()),
          });
        }
      });
    } catch {
      setIsLoadingSubmit(false);
      setHasUpdateError(true);
      trackEventBE('be_om_select_payment_method', {
        status: 'failed',
        message: 'Failed to update order with selected payment method',
        data: getTrackableFormValues(getValues()),
      });
    }
  };

  return (
    <Stack gap={'10'} alignX="start">
      {hasUpdateError && <DataErrorNotification />}
      <Stack direction="column" gap="2">
        <Heading as="h1" size="M">
          {paymentMethodFields.title.value}
        </Heading>
        <div>
          <RichText html={paymentMethodFields.subTitle.value} />
        </div>
      </Stack>
      {/* eslint-disable-next-line dxp-rules/no-inline-css */}
      <div style={{ alignSelf: 'stretch' }}>
        <Stack gap="6" alignX="start">
          <Bucket title={paymentMethodFields.choosePaymentMethodTitle.value}>
            <Bucket.Content>
              <InputSelect
                options={paymentMethodFields.paymentMethodsList.value.options.map(option => ({
                  label: option.label,
                  value: option.name,
                }))}
                label={paymentMethodFields.paymentMethodsList.value.label}
                isOptional={false}
                placeholder=""
                hint={paymentMethodFields?.paymentMethodsList?.value?.hint ?? ''}
                error={errors.selectPaymentMethod?.paymentMethod?.message as string}
                {...register('selectPaymentMethod.paymentMethod')}
              />
              {paymentMethod === ORDER_MANAGEMENT_PAYMENT_METHODS.DIRECT_DEBIT && (
                <IbanNumberInput
                  label={paymentMethodFields.accountNumberFormField.value.label}
                  hint={paymentMethodFields.accountNumberText?.value}
                  placeholder={paymentMethodFields.accountNumberFormField.value.placeholder}
                  errorMessage={paymentMethodFields.accountNumberFormField.value.validationMessage}
                  defaultValue={getValues('selectPaymentMethod.ibanNumber') || ''}
                  showErrorMessage={!!errors.selectPaymentMethod?.ibanNumber?.message?.length}
                  {...register(`selectPaymentMethod.ibanNumber`)}
                />
              )}
              <AlertDialog
                title={paymentMethodFields.discountMightBeLostDialog.value.title}
                description={<RichText html={paymentMethodFields.discountMightBeLostDialog.value.content} />}
                isOpen={showPromotionWarning}
                setOpen={setShowPromotionWarning}
                confirmText={
                  paymentMethodFields.discountMightBeLostDialog.value.submitButtonText || 'OK [not in sitecore]'
                }
                onConfirm={() => {
                  setShowPromotionWarning(false);
                  onNextClick();
                }}
                onDeny={() => {
                  setShowPromotionWarning(false);
                }}
                denyText={
                  paymentMethodFields.discountMightBeLostDialog.value.cancelButtonText || 'Cancel [not in sitecore]'
                }
              />
            </Bucket.Content>
          </Bucket>
        </Stack>
      </div>
      {error && <NotificationBox {...error} />}
      <Button onClick={checkDialogTrigger} isLoading={isLoadingSubmit}>
        {fields.navigation.nextButtonLabel.value}
      </Button>
    </Stack>
  );
};
